import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import RespondentDetailSheet from '../dialogs/RespondentDetailSheet';
import { prettyDate } from '../surveyUiUtils';
import { Eye, Flag, ShieldCheck, AlertTriangle } from 'lucide-react';

const ConsultantTab = ({ campaign, responses = [], facilityList = [], report, onFlagResponse, loading }) => {
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const facilityById = useMemo(
    () => new Map((facilityList || []).map((f) => [String(f.id), f])),
    [facilityList],
  );

  const invitationById = useMemo(() => {
    const map = new Map();
    for (const inv of campaign?.invitations || []) {
      map.set(String(inv.id), inv);
    }
    return map;
  }, [campaign]);

  const submitted = useMemo(
    () =>
      responses
        .filter((r) => !r.isDraft && r.submittedAt)
        .map((r) => ({
          ...r,
          invitation: invitationById.get(String(r.invitationId || '')) || null,
          facilityName: facilityById.get(String(r.facilityId || ''))?.name || '—',
          isFlagged: Array.isArray(r.validationErrors) && r.validationErrors.length > 0,
          flagReason: Array.isArray(r.validationErrors) && r.validationErrors.length > 0
            ? r.validationErrors.map((e) => e.message || e).join(', ')
            : null,
          wfhTco2e: r.wfhTco2e ?? r.totals?.wfh ?? null,
          commuteTco2e: r.commuteTco2e ?? r.totals?.commuting ?? null,
        })),
    [responses, invitationById, facilityById],
  );

  const methodologyFlags = useMemo(() => {
    const flags = [];
    const unknownFuel = submitted.filter((r) => {
      const fuel = String(r.responseData?.wfh_heating_fuel || '').toLowerCase();
      return fuel === 'unknown' || fuel === 'dont_know';
    });
    if (unknownFuel.length > 0) {
      flags.push({ title: "Don't Know fuel → 0 kWh", note: `${unknownFuel.length} respondent${unknownFuel.length !== 1 ? 's' : ''}. Review heating assumptions.` });
    }
    const longCommute = submitted.filter((r) => {
      const legs = r.responseData?.commute_legs || [];
      return legs.some((l) => Number(l.leg_distance_km_one_way || 0) > 200);
    });
    if (longCommute.length > 0) {
      flags.push({ title: 'Long commute >200 km', note: `${longCommute.length} respondent${longCommute.length !== 1 ? 's' : ''}. Manual review recommended.` });
    }
    return flags;
  }, [submitted]);

  const openSheet = (row) => {
    setSelectedResponse(row);
    setSheetOpen(true);
  };

  return (
    <div className='space-y-6'>
      <Alert>
        <ShieldCheck size={14} strokeWidth={1.5} />
        <AlertTitle>Consultant audit access</AlertTitle>
        <AlertDescription>
          You can view raw responses, calculated per-component values, and the emission factors applied. Flag or exclude
          anomalous responses before final calculation. This tab is only visible to CYF consultants and whitelisted admins.
        </AlertDescription>
      </Alert>

      <div className='grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6'>
        <Card>
          <CardHeader>
            <CardTitle className='text-base'>Submitted responses</CardTitle>
            <p className='text-xs text-muted-foreground mt-1'>
              Click a row to inspect raw answers, per-component values, and the factor year applied.
            </p>
          </CardHeader>
          <Table wrapperClassName='rounded-none border-0 shadow-none'>
            <TableHeader>
              <TableRow>
                <TableHead>Respondent</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Flags</TableHead>
                <TableHead className='text-right'>WFH tCO2e</TableHead>
                <TableHead className='text-right'>Commute tCO2e</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {submitted.length > 0 ? (
                submitted.map((row) => (
                  <TableRow key={row.id} className='cursor-pointer' onClick={() => openSheet(row)}>
                    <TableCell>
                      <div className='font-medium'>{row.invitation?.employeeName || '—'}</div>
                      <div className='text-xs text-muted-foreground'>{row.invitation?.employeeEmail || row.id}</div>
                    </TableCell>
                    <TableCell className='text-sm'>{row.facilityName}</TableCell>
                    <TableCell className='text-xs text-muted-foreground'>{prettyDate(row.submittedAt)}</TableCell>
                    <TableCell>
                      {row.flagged ? (
                        <Badge variant='warning' size='sm'>
                          <AlertTriangle size={10} strokeWidth={1.5} />
                          {row.flagReason}
                        </Badge>
                      ) : (
                        <Badge variant='success' size='sm'>OK</Badge>
                      )}
                    </TableCell>
                    <TableCell className='text-right tabular-nums text-sm'>
                      {row.wfhTco2e != null ? row.wfhTco2e.toFixed(3) : '—'}
                    </TableCell>
                    <TableCell className='text-right tabular-nums text-sm'>
                      {row.commuteTco2e != null ? row.commuteTco2e.toFixed(3) : '—'}
                    </TableCell>
                    <TableCell className='text-right' onClick={(e) => e.stopPropagation()}>
                      <div className='flex items-center justify-end gap-1'>
                        <Button variant='ghost' size='icon' title='Inspect' onClick={() => openSheet(row)}>
                          <Eye size={14} strokeWidth={1.5} />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon'
                          title={row.flagged ? 'Remove flag' : 'Flag for review'}
                          loading={Boolean(loading?.flagResponse)}
                          onClick={() =>
                            onFlagResponse?.(
                              row.id,
                              row.flagged
                                ? { flagged: false }
                                : { flagged: true, note: 'Flagged from consultant tab' },
                            )}>
                          <Flag
                            size={14}
                            strokeWidth={1.5}
                            fill={row.flagged ? 'currentColor' : 'none'}
                          />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className='text-center text-sm text-muted-foreground py-8'>
                    No submitted responses yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>

        <div className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle className='text-sm'>Engine &amp; factors</CardTitle>
            </CardHeader>
            <CardContent className='space-y-2 text-xs'>
              {[
                { label: 'Engine version', value: report?.engineVersion || '—' },
                { label: 'DEFRA factor year', value: campaign?.reportingYear || '—' },
                { label: 'Reporting period', value: campaign?.reportingPeriodStart && campaign?.reportingPeriodEnd ? `${campaign.reportingPeriodStart} → ${campaign.reportingPeriodEnd}` : '—' },
                { label: 'Last recalculated', value: prettyDate(campaign?.closedAt) },
              ].map(({ label, value }) => (
                <div key={label} className='flex items-center justify-between'>
                  <span className='text-muted-foreground'>{label}</span>
                  <span className='font-mono'>{value}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {methodologyFlags.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className='text-sm'>Methodology flags</CardTitle>
              </CardHeader>
              <CardContent className='space-y-3 text-xs'>
                {methodologyFlags.map((flag) => (
                  <div key={flag.title} className='flex gap-2'>
                    <AlertTriangle size={12} strokeWidth={1.5} className='text-warning flex-shrink-0 mt-0.5' />
                    <div>
                      <div className='font-semibold'>{flag.title}</div>
                      <div className='text-muted-foreground'>{flag.note}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <RespondentDetailSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        response={selectedResponse}
        invitation={selectedResponse?.invitation}
        facilityName={selectedResponse?.facilityName}
        onFlag={() => {
          if (selectedResponse) {
            onFlagResponse?.(selectedResponse.id, { flagged: true, note: 'Flagged from consultant tab' });
          }
        }}
        onExclude={() => {
          if (selectedResponse) {
            onFlagResponse?.(selectedResponse.id, { excluded: true, note: 'Excluded from consultant tab' });
          }
        }}
        loading={loading}
      />
    </div>
  );
};

export default ConsultantTab;
